// v2 dragonfly — a darter built like the real insect: a long two-segment
// abdomen with an up-curled club tip, a chunky striped thorax, two big
// spherical compound eyes meeting over the head, and four crisp flat wing
// blades — fronts swept a touch forward, rears back — each carrying the dark
// pterostigma cell near its tip. Three liveries: v0 teal darter, v1 crimson
// darter, v2 blue dasher. Ported 1:1 from age-of-mcp's v2/dragonfly.ts.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const LIVERIES = [
  { abdo: "#1f9e8a", thorax: "#26b89e", stripe: "#0e6e5e", eye: "#14352c" },
  { abdo: "#c84048", thorax: "#a83438", stripe: "#6e2024", eye: "#2c1416" },
  { abdo: "#3a78c8", thorax: "#4a86d2", stripe: "#1f4a86", eye: "#14202c" },
];

function dragonflyParts(v: number): Part[] {
  const c = LIVERIES[v % LIVERIES.length]!;
  const parts: Part[] = [
    // two-segment abdomen, tip curling up into a small club
    { geometry: new THREE.CylinderGeometry(0.0032, 0.0026, 0.034, 4), color: c.abdo,
      position: [-0.002, 0.02, 0], rotation: [0, 0, Math.PI / 2] },
    { geometry: new THREE.CylinderGeometry(0.0025, 0.0016, 0.032, 4), color: c.abdo,
      position: [-0.034, 0.022, 0], rotation: [0, 0, Math.PI / 2 - 0.12] },
    { geometry: new THREE.IcosahedronGeometry(0.0028, 0), color: c.stripe,
      position: [-0.052, 0.026, 0] },
    // chunky thorax with a dark dorsal stripe
    { geometry: new THREE.IcosahedronGeometry(0.0085, 0), color: c.thorax,
      position: [0.018, 0.021, 0], scale: [1.3, 1.0, 0.9] },
    { geometry: new THREE.BoxGeometry(0.012, 0.0016, 0.005), color: c.stripe,
      position: [0.018, 0.0295, 0] },
    // two big compound eyes meeting over the head
    { geometry: new THREE.IcosahedronGeometry(0.006, 0), color: c.eye,
      position: [0.032, 0.024, 0.0045] },
    { geometry: new THREE.IcosahedronGeometry(0.006, 0), color: c.eye,
      position: [0.032, 0.024, -0.0045] },
  ];
  // four crisp wing blades with pterostigma cells near the tips
  for (const s of [1, -1]) {
    parts.push(
      { geometry: new THREE.BoxGeometry(0.013, 0.0015, 0.05), color: "#d6eef2",
        position: [0.022, 0.0275, s * 0.0305], rotation: [0, s * 0.12, 0] },
      { geometry: new THREE.BoxGeometry(0.012, 0.0015, 0.046), color: "#def2f4",
        position: [0.004, 0.0265, s * 0.029], rotation: [0, -s * 0.18, 0] },
      { geometry: new THREE.BoxGeometry(0.005, 0.002, 0.006), color: c.stripe,
        position: [0.027, 0.0282, s * 0.049] },
      { geometry: new THREE.BoxGeometry(0.005, 0.002, 0.006), color: c.stripe,
        position: [-0.004, 0.0272, s * 0.046] },
    );
  }
  return parts;
}

export const dragonflyV2 = defineModel({
  id: "dragonfly-v2",
  batchId: "fable",
  name: "Dragonfly (v2)",
  tags: ["creature","dragonfly","low-poly","age-of-mcp","fable"],
  summary: "Low-poly dragonfly.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.05,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => dragonflyParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => dragonflyParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => dragonflyParts(2) },
  ],
  animations: [],
});
